import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Calendar, Filter, Download, TrendingUp, TrendingDown, RefreshCw, Search } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const RawMaterialMovement = () => {
  const navigate = useNavigate();
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessInfo, setBusinessInfo] = useState(null);
  
  // Filters - default to last 30 days
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
      from: thirtyDaysAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };
  
  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    product_id: '',
    movement_type: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRawMaterials();
    fetchBusinessInfo();
  }, []);

  useEffect(() => {
    fetchMovements();
  }, [filters]);

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
    } catch (err) {
      console.error('Error fetching business info:', err);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRawMaterials(response.data);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
    }
  };

  const fetchMovements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = {
        raw_materials_only: "true"  // Always show only raw materials
      };
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to;
      if (filters.product_id) params.product_id = filters.product_id;
      if (filters.movement_type) params.movement_type = filters.movement_type;
      
      const response = await axios.get(`${API}/raw-material-movement`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setMovements(response.data.movements || []);
      setSummary(response.data.summary || {});
    } catch (err) {
      console.error('Error fetching movements:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generatePDF = () => {
    if (movements.length === 0) return;

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(businessInfo?.business_name || 'GPOS', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Raw Material Movement Report', pageWidth / 2, 22, { align: 'center' });

    // Period
    doc.setFontSize(10);
    doc.text(`Period: ${formatDate(filters.from)} to ${formatDate(filters.to)}`, 14, 32);
    if (filters.product_id) {
      const product = rawMaterials.find(p => p.id === filters.product_id);
      doc.text(`Product: ${product?.name || 'Selected Product'}`, 14, 38);
    }

    // Summary Box
    doc.setFillColor(245, 245, 245);
    doc.rect(14, 44, pageWidth - 28, 12, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Stock In: ${summary?.total_in || 0}`, 20, 52);
    doc.text(`Stock Out: ${summary?.total_out || 0}`, 80, 52);
    doc.text(`Net: ${summary?.net_movement || 0}`, 140, 52);
    doc.text(`Records: ${summary?.total_records || 0}`, 200, 52);

    // Table
    const tableData = movements.map(m => [
      formatDateTime(m.date),
      m.type === 'in' ? 'Stock In' : 'Stock Out',
      m.product_name,
      m.quantity?.toFixed(2) || '0',
      m.unit || 'PCS',
      m.reference || '-',
      m.user || '-',
      m.notes || '-'
    ]);

    doc.autoTable({
      startY: 60,
      head: [['Date', 'Type', 'Product', 'Qty', 'Unit', 'Reference', 'User', 'Notes']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [124, 58, 237], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 22 },
        2: { cellWidth: 40 },
        3: { cellWidth: 18, halign: 'right' },
        4: { cellWidth: 15 },
        5: { cellWidth: 35 },
        6: { cellWidth: 25 },
        7: { cellWidth: 45 }
      }
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, finalY);

    doc.save(`RawMaterial_Movement_${filters.from}_to_${filters.to}.pdf`);
  };

  const resetFilters = () => {
    setFilters({ ...getDefaultDates(), product_id: '', movement_type: '' });
  };

  return (
    <div data-testid="raw-material-movement-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/gposp/raw-material-usage')}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Raw Material Movement Report</h1>
            <p className="text-slate-500">Stock In (GRN) and Stock Out (Usage) for Raw Materials</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchMovements}
            className="p-2 border border-slate-200 rounded-xl hover:bg-slate-50"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          {movements.length > 0 && (
            <button
              onClick={generatePDF}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
            >
              <Download size={18} />
              Export PDF
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <Filter size={18} />
            Filters
          </button>
          
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Calendar size={16} />
            {formatDate(filters.from)} - {formatDate(filters.to)}
            {filters.product_id && (
              <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded">
                {rawMaterials.find(p => p.id === filters.product_id)?.name}
              </span>
            )}
            {filters.movement_type && (
              <span className={`px-2 py-0.5 rounded ${
                filters.movement_type === 'in' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {filters.movement_type === 'in' ? 'Stock In Only' : 'Stock Out Only'}
              </span>
            )}
          </div>

          <button onClick={resetFilters} className="text-sm text-violet-600 hover:underline">
            Reset Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">From Date</label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">To Date</label>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Raw Material
              </label>
              <select
                value={filters.product_id}
                onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">All Raw Materials</option>
                {rawMaterials.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Movement Type</label>
              <select
                value={filters.movement_type}
                onChange={(e) => setFilters({ ...filters, movement_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">All Movements</option>
                <option value="in">Stock In (GRN)</option>
                <option value="out">Stock Out (Usage)</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setShowFilters(false)}
                className="w-full py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="text-green-500" size={20} />
              <p className="text-sm text-slate-500">Stock In</p>
            </div>
            <p className="text-2xl font-bold text-green-600">{summary.total_in?.toFixed(2) || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="text-red-500" size={20} />
              <p className="text-sm text-slate-500">Stock Out</p>
            </div>
            <p className="text-2xl font-bold text-red-600">{summary.total_out?.toFixed(2) || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500 mb-2">Net Movement</p>
            <p className={`text-2xl font-bold ${summary.net_movement >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
              {summary.net_movement >= 0 ? '+' : ''}{summary.net_movement?.toFixed(2) || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500 mb-2">Total Records</p>
            <p className="text-2xl font-bold text-slate-700">{summary.total_records || 0}</p>
          </div>
        </div>
      )}

      {/* Movement Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-violet-200 border-t-violet-600"></div>
          </div>
        ) : movements.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="mx-auto mb-4 text-slate-300" size={48} />
            <p className="text-slate-500">No movement records found for the selected filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date & Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Product</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {movements.map((movement, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{formatDateTime(movement.date)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        movement.type === 'in' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {movement.type === 'in' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {movement.type === 'in' ? 'Stock In' : 'Stock Out'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">{movement.product_name}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${movement.type === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                        {movement.type === 'in' ? '+' : '-'}{movement.quantity?.toFixed(2)}
                      </span>
                      <span className="text-slate-400 ml-1">{movement.unit}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-violet-600 font-medium">{movement.reference}</span>
                      {movement.supplier && (
                        <p className="text-xs text-slate-500">{movement.supplier}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {movement.user}
                      {movement.verified_by && (
                        <p className="text-xs text-green-600">✓ {movement.verified_by}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate">{movement.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RawMaterialMovement;
